/*
  Back-in-stock signup (markup: snippets/back-in-stock.liquid).

  Shows under the buy button while the selected variant is sold out and POSTs the shopper's
  email + consent to the endpoint in data-endpoint, which emails the team. Version 1 only:
  nothing is stored and the shopper is not notified automatically.
*/
class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.bis__form');
    if (!this.form) return;

    this.emailInput = this.form.elements['contact[email]'];
    this.consentInput = this.form.elements['contact[Einwilligung]'];
    this.alert = this.querySelector('[data-alert]');
    this.submitButton = this.querySelector('[data-submit]');
    this.successBox = this.querySelector('.bis__success');
    this.variantId = this.dataset.variantId;
    this.busy = false;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Capture phase on window: runs before the theme's hover-button handler on the form, so an
    // invalid submit is stopped before anything else reacts to it.
    window.addEventListener('submit', this.onSubmit.bind(this), { capture: true, signal });
    // The theme dispatches `variant:change` on the product form (not bubbling), so listen while capturing.
    document.addEventListener('variant:change', this.onVariantChange.bind(this), { capture: true, signal });
    this.form.addEventListener('input', this.onFieldInput.bind(this), { signal });
    this.form.addEventListener('change', this.onFieldInput.bind(this), { signal });

    this.updateProductUrl();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  // Variant switching ------------------------------------------------

  onVariantChange(event) {
    // getAttribute, not `.id`: the product form has an <input name="id"> that shadows form.id.
    const sourceFormId = event.target.getAttribute && event.target.getAttribute('id');
    if (sourceFormId && this.dataset.productFormId && sourceFormId !== this.dataset.productFormId) return;

    const variant = event.detail && event.detail.variant;
    if (!variant) return;

    const changed = String(variant.id) !== this.variantId;
    this.variantId = String(variant.id);
    this.form.elements['contact[Variante]'].value = variant.title || '';
    this.form.elements['contact[Varianten-ID]'].value = variant.id;
    this.form.elements['contact[SKU]'].value = variant.sku || '';
    this.dataset.variantId = this.variantId;
    this.updateProductUrl();

    this.hidden = variant.available !== false;

    // A different sold-out variant is a new request: offer the form again, keep the typed email.
    if (changed) this.reset({ keepEmail: true });
  }

  updateProductUrl() {
    const path = this.dataset.productPath || window.location.pathname;
    const url = new URL(path, window.location.origin);
    if (this.variantId) url.searchParams.set('variant', this.variantId);
    this.form.elements['contact[Produkt-URL]'].value = url.toString();
  }

  reset({ keepEmail = false } = {}) {
    const email = this.emailInput.value;
    this.form.reset();
    if (keepEmail) this.emailInput.value = email;
    this.updateProductUrl();
    this.clearErrors();
    this.setState('form');
  }

  setState(state) {
    this.dataset.state = state;
  }

  // Validation -------------------------------------------------------

  fieldFor(input) {
    return input.closest('.bis__field') || input.closest('.bis__consent-wrap');
  }

  clearErrors() {
    this.querySelectorAll('.has-error').forEach((el) => el.classList.remove('has-error'));
    this.form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    if (this.alert) this.alert.hidden = true;
  }

  onFieldInput(event) {
    if (event.target.checkValidity && event.target.checkValidity()) {
      this.fieldFor(event.target)?.classList.remove('has-error');
      event.target.removeAttribute('aria-invalid');
    }
    if (this.alert) this.alert.hidden = true;
  }

  validate() {
    let firstInvalid = null;

    for (const input of [this.emailInput, this.consentInput]) {
      // Trim before checking: a value of only spaces must not pass `required`.
      if (input.type === 'email') input.value = input.value.trim();

      const valid = input.checkValidity();
      this.fieldFor(input)?.classList.toggle('has-error', !valid);
      if (valid) {
        input.removeAttribute('aria-invalid');
      } else {
        input.setAttribute('aria-invalid', 'true');
        firstInvalid = firstInvalid || input;
      }
    }

    firstInvalid?.focus();
    return !firstInvalid;
  }

  // Submit -----------------------------------------------------------

  onSubmit(event) {
    if (event.target !== this.form) return;

    event.preventDefault();
    event.stopPropagation();

    if (this.busy || !this.validate()) return;

    this.setBusy(true);
    if (this.alert) this.alert.hidden = true;

    fetch(this.dataset.endpoint, { method: 'POST', body: new FormData(this.form) })
      .then((response) => {
        if (!response.ok) throw new Error(`Back-in-stock signup failed: ${response.status}`);
        this.setState('success');
        this.successBox?.setAttribute('tabindex', '-1');
        this.successBox?.focus();
      })
      .catch(() => {
        if (this.alert) this.alert.hidden = false;
      })
      .finally(() => this.setBusy(false));
  }

  setBusy(busy) {
    this.busy = busy;
    if (!this.submitButton) return;

    this.submitButton.toggleAttribute('disabled', busy);
    if (busy) {
      this.submitButton.setAttribute('aria-busy', 'true');
    } else {
      this.submitButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
